import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  ICategoriaIncidentes,
  ICategoriaIncidentesPagingRes,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_CATEGORIA_INCIDENTES_PAGING,
  MOCK_CATEGORIA_INCIDENTES,
  mockAxiosResponse,
} from '@/shared/actions/erp/tickets-soporte-cliente/tickets-mock-data';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCategoriaIncidentes = ({
  page = 1,
  page_size = 10,
  nombre = '',
  numero_referencia,
  descripcion,
  departamento,
}: GetCategoriaIncidentesParams) => {
  return useQuery({
    queryKey: ['categoriaincidencia', page, nombre, numero_referencia, descripcion,departamento, page_size],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_CATEGORIA_INCIDENTES_PAGING);
      return getCategoriaIncidentess({ page, nombre,numero_referencia, descripcion,departamento, page_size });
    },
  });
};

export const useFetchCategoriaIncidentesEnabled = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetCategoriaIncidentesParams>) => {
  return useQuery({
    queryKey: ['categoriaincidencia', params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_CATEGORIA_INCIDENTES_PAGING);
      return getCategoriaIncidentess(params);
    },
    enabled,
  });
};

export const useGetCategoriaIncidentes = (id: string) => {
  return useQuery({
    queryKey: ['categoriaincidencias', id],
    queryFn: async () => {
      if (USE_MOCK) {
        const item = MOCK_CATEGORIA_INCIDENTES.find((c) => c.id_categoria_incidencia === id) || MOCK_CATEGORIA_INCIDENTES[0];
        return mockAxiosResponse(item);
      }
      return getCategoriaIncidentes(id);
    },
  });
};

export const useCreateCategoriaIncidentes = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCategoriaIncidentes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoriaincidencia'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Categoria de incidencia creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateCategoriaIncidentes = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCategoriaIncidentes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoriaincidencia'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ??
            'Categoria de incidencia actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetCategoriaIncidentesParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  numero_referencia?:string;
  descripcion?:string;
  departamento?:string;
};

export type CreateCategoriaIncidentesParams = Omit<
  ICategoriaIncidentes,
  'id_categoria_incidencia'
>;
export type UpdateCategoriaIncidentesParams = {
  id: string;
  data: CreateCategoriaIncidentesParams;
};

export const getCategoriaIncidentess = async (
  params?: GetCategoriaIncidentesParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ICategoriaIncidentesPagingRes>(
    `${urlBackend}api/v1/categoriaincidencia/?${queryParams}`,
    true
  );
};

export const getCategoriaIncidentes = async (id: string) => {
  try {
    return await get<ICategoriaIncidentes>(
      `${urlBackend}api/v1/categoriaincidencia/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createCategoriaIncidentes = async (
  data: CreateCategoriaIncidentesParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ICategoriaIncidentes>(
    `${urlBackend}api/v1/categoriaincidencia/`,
    data,
    true
  );
};
export const updateCategoriaIncidentes = async ({
  id,
  data,
}: UpdateCategoriaIncidentesParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ICategoriaIncidentes>(
    `${urlBackend}api/v1/categoriaincidencia/${id}/`,
    data,
    true
  );
};
